/**
 * React Hook — Bridges the vanilla TS simulationStore into React.
 *
 * Subscribes to store state changes and triggers re-renders.
 * Provides typed access to all store actions and state.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { simulationStore, type SimulationState } from '../store/simulationStore';
import type {
  RunSimulationRequest,
  ReplaySimulationRequest,
  StressSimulationRequest,
  SimulationResult,
} from '../types/simulation';
import {
  cancelActiveSimulation,
  pollSimulation,
  setActivePollingController,
  isSimulationActive,
} from '../services/simulationAdapter';

export function useSimulationStore() {
  const [state, setState] = useState<SimulationState>(simulationStore.getState());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const unsub = simulationStore.subscribe((next) => {
      if (mountedRef.current) {
        setState(next);
      }
    });

    return () => {
      mountedRef.current = false;
      unsub();
    };
  }, []);

  // ── Actions ─────────────────────────────────────────────────

  const executeRunSimulation = useCallback(async (request: RunSimulationRequest) => {
    return simulationStore.executeRunSimulation(request);
  }, []);

  const executeReplaySimulation = useCallback(async (request: ReplaySimulationRequest) => {
    return simulationStore.executeReplaySimulation(request);
  }, []);

  const executeStressTest = useCallback(async (request: StressSimulationRequest) => {
    return simulationStore.executeStressTest(request);
  }, []);

  const cancel = useCallback(() => {
    simulationStore.cancelSimulation();
  }, []);

  const reset = useCallback(() => {
    simulationStore.reset();
  }, []);

  const startPolling = useCallback((simulationId: string, onUpdate?: (r: SimulationResult) => void) => {
    const controller = pollSimulation(simulationId, {
      intervalMs: 1500,
      timeoutMs: 60_000,
      onUpdate: (result) => {
        if (!mountedRef.current) return;
        if (result.status === 'partial' || result.status === 'running') {
          simulationStore.updatePartialResults(result);
        }
        onUpdate?.(result);
      },
      onComplete: (result) => {
        if (!mountedRef.current) return;
        simulationStore.completeSimulation(result);
      },
      onError: (error) => {
        if (!mountedRef.current) return;
        simulationStore.failSimulation(error);
      },
    });
    setActivePollingController(controller);
    return controller;
  }, []);

  return {
    // State
    ...state,
    isActive: isSimulationActive(),

    // Actions
    executeRunSimulation,
    executeReplaySimulation,
    executeStressTest,
    cancel,
    reset,
    startPolling,

    // Derived helpers
    isIdle: state.status === 'idle',
    isLoading: state.status === 'loading',
    isRunning: state.status === 'running',
    isPartial: state.status === 'partial',
    isSuccess: state.status === 'success',
    isError: state.status === 'error',
    hasResults: state.results !== null,
    hasPartialResults: state.partialResults !== null,
  };
}

export type UseSimulationStore = ReturnType<typeof useSimulationStore>;
